import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ColorPicker } from 'primereact/colorpicker';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { projectFoldersAPI } from '../services/api';
import { ProjectFolder } from '../types';

const ProjectFolders: React.FC = () => {
  const [folders, setFolders] = useState<ProjectFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingFolder, setEditingFolder] = useState<ProjectFolder | null>(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6b7280',
    icon: 'pi pi-folder',
    order: 0
  });

  useEffect(() => {
    loadFolders();
  }, []);

  const loadFolders = async () => {
    setLoading(true);
    try {
      const data = await projectFoldersAPI.getAll();
      setFolders(data);
    } catch (err: any) {
      setError(err.message || 'Greška pri učitavanju foldera');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingFolder(null);
    setFormData({
      name: '',
      description: '',
      color: '#6b7280',
      icon: 'pi pi-folder',
      order: folders.length
    });
    setError('');
    setDialogVisible(true);
  };

  const handleEdit = (folder: ProjectFolder) => {
    setEditingFolder(folder);
    setFormData({
      name: folder.name,
      description: folder.description || '',
      color: folder.color,
      icon: folder.icon,
      order: folder.order
    });
    setError('');
    setDialogVisible(true);
  };

  const handleDelete = (folder: ProjectFolder) => {
    confirmDialog({
      message: `Da li ste sigurni da želite da obrišete folder "${folder.name}"? Projekti iz ovog foldera će biti vraćeni u glavni prikaz.`,
      header: 'Potvrda brisanja',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await projectFoldersAPI.delete(folder._id);
          await loadFolders();
        } catch (err: any) {
          setError(err.message || 'Greška pri brisanju foldera');
        }
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Naziv foldera je obavezan');
      return;
    }

    try {
      if (editingFolder) {
        await projectFoldersAPI.update(editingFolder._id, formData);
      } else {
        await projectFoldersAPI.create(formData);
      }
      setDialogVisible(false);
      await loadFolders();
    } catch (err: any) {
      setError(err.message || 'Greška pri čuvanju foldera');
    }
  };

  const colorBodyTemplate = (rowData: ProjectFolder) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <div
          style={{
            width: '24px',
            height: '24px',
            backgroundColor: rowData.color,
            borderRadius: '4px',
            border: '1px solid #ddd'
          }}
        />
        <span>{rowData.color}</span>
      </div>
    );
  };

  const iconBodyTemplate = (rowData: ProjectFolder) => {
    return <i className={rowData.icon} style={{ fontSize: '1.2rem' }} />;
  };

  const actionsBodyTemplate = (rowData: ProjectFolder) => {
    return (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleEdit(rowData)}
          tooltip="Izmeni"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          onClick={() => handleDelete(rowData)}
          tooltip="Obriši"
        />
      </div>
    );
  };

  return (
    <div style={{ padding: '2rem' }}>
      <ConfirmDialog />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1>Upravljaj folderima</h1>
        <Button
          label="Novi folder"
          icon="pi pi-plus"
          onClick={handleCreate}
        />
      </div>

      {error && (
        <Message severity="error" text={error} style={{ marginBottom: '1rem' }} />
      )}

      <DataTable
        value={folders}
        loading={loading}
        paginator
        rows={10}
        emptyMessage="Nema foldera"
      >
        <Column field="name" header="Naziv" sortable />
        <Column field="description" header="Opis" />
        <Column field="color" header="Boja" body={colorBodyTemplate} />
        <Column field="icon" header="Ikona" body={iconBodyTemplate} />
        <Column field="order" header="Redosled" sortable />
        <Column body={actionsBodyTemplate} header="Akcije" style={{ width: '120px' }} />
      </DataTable>

      <Dialog
        header={editingFolder ? 'Izmeni folder' : 'Novi folder'}
        visible={dialogVisible}
        style={{ width: '500px' }}
        onHide={() => setDialogVisible(false)}
        footer={
          <div>
            <Button label="Otkaži" icon="pi pi-times" onClick={() => setDialogVisible(false)} className="p-button-text" />
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
          </div>
        }
      >
        {error && <Message severity="error" text={error} style={{ marginBottom: '1rem' }} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label htmlFor="name" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Naziv <span style={{ color: 'red' }}>*</span>
            </label>
            <InputText
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{ width: '100%' }}
              placeholder="npr. IT Projekti"
            />
          </div>
          <div>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Opis
            </label>
            <InputTextarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={{ width: '100%' }}
              rows={3}
              placeholder="Opis foldera (opciono)"
            />
          </div>
          <div>
            <label htmlFor="color" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Boja
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <ColorPicker
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: '#' + e.value })}
              />
              <InputText
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                style={{ flex: 1 }}
                placeholder="#6366f1"
              />
            </div>
          </div>
          <div>
            <label htmlFor="icon" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Ikona (PrimeReact ikona klasa)
            </label>
            <InputText
              id="icon"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              style={{ width: '100%' }}
              placeholder="pi pi-folder"
            />
            <small style={{ color: '#666' }}>
              Primeri: pi pi-folder, pi pi-desktop, pi pi-palette, pi pi-code
            </small>
          </div>
          <div>
            <label htmlFor="order" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Redosled
            </label>
            <InputText
              id="order"
              type="number"
              value={formData.order.toString()}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProjectFolders;

